import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { get, patch, errMsg, inr, PRODUCT_OPTIONS } from "@/lib/api";
import { PageHeader, Loading, useFetch, StatusBadge } from "@/components/kit";

const COLS = [
  ["New", "NEW", ["NEW"]], ["Contacted", "CONTACTED", ["CONTACTED"]], ["Follow-up", "FOLLOW_UP", ["FOLLOW_UP"]],
  ["Documents Pending", "DOCUMENT_PENDING", ["DOCUMENT_PENDING"]], ["KYC", "KYC_PENDING", ["ELIGIBILITY_CHECK", "KYC_PENDING", "KYC_COMPLETED"]],
  ["Lender Processing", "LENDER_SUBMITTED", ["LENDER_SUBMITTED"]], ["Offer Received", "OFFER_RECEIVED", ["OFFER_RECEIVED", "CUSTOMER_ACCEPTED"]],
  ["Sanction", "SANCTIONED", ["SANCTIONED"]], ["Disbursement", "DISBURSEMENT_PENDING", ["DISBURSEMENT_PENDING"]],
  ["Disbursed", "DISBURSED", ["DISBURSED", "PAYOUT_PENDING", "PAYOUT_PAID", "CLOSED"]], ["Rejected", "REJECTED", ["REJECTED"]], ["Dropped", "DROPPED", ["DROPPED"]],
];

export default function Kanban() {
  const nav = useNavigate();
  const [product, setProduct] = useState("");
  const { data, setData, reload } = useFetch(() => get("/leads/kanban", product ? { product } : {}), [product]);
  const [over, setOver] = useState(null);
  if (!data) return <Loading />;
  const drop = async (e, status) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const lead = data.find((l) => l.id === id);
    if (!lead || lead.status === status) return;
    setData(data.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await patch(`/leads/${id}`, { status }); toast.success(`${lead.lead_code} → ${status.replace(/_/g, " ")}`); } catch (er) { toast.error(errMsg(er)); reload(); }
  };
  return (
    <div>
      <PageHeader title="CRM Kanban" subtitle="Drag cards between columns to update lead status. Every move is audited.">
        <select data-testid="kanban-product-filter" className="fo-input h-9 w-auto" value={product} onChange={(e) => setProduct(e.target.value)}><option value="">All products</option>{PRODUCT_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </PageHeader>
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
        {COLS.map(([title, status, group]) => {
          const items = data.filter((l) => group.includes(l.status));
          return (
            <div key={status} data-testid={`kanban-col-${status}`} onDragOver={(e) => { e.preventDefault(); setOver(status); }} onDragLeave={() => setOver(null)} onDrop={(e) => drop(e, status)}
              className={`kanban-col w-64 shrink-0 rounded-xl border border-slate-200 bg-slate-100/70 p-2 ${over === status ? "drag-over" : ""}`}>
              <div className="mb-2 flex items-center justify-between px-1"><p className="text-xs font-bold uppercase tracking-wider text-slate-600">{title}</p><span className="rounded-full bg-white px-2 text-xs font-semibold">{items.length}</span></div>
              <div className="min-h-[200px] space-y-2">
                {items.map((l) => (
                  <div key={l.id} draggable data-testid={`kanban-card-${l.lead_code}`} onDragStart={(e) => e.dataTransfer.setData("text/plain", l.id)} onClick={() => nav(`/crm/leads/${l.id}`)}
                    className="cursor-grab rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:border-blue-300 active:cursor-grabbing">
                    <div className="flex items-center justify-between"><p className="font-mono text-[10px] text-slate-400">{l.lead_code}</p><span className="text-[10px] font-bold text-blue-900">{l.score}</span></div>
                    <p className="text-sm font-semibold">{l.customer_name}</p>
                    <p className="text-xs text-slate-500">{l.product} · {inr(l.loan_amount)}</p>
                    <div className="mt-2 flex items-center justify-between"><StatusBadge value={l.status} /><span className="truncate text-[10px] text-slate-400">{l.assigned_employee_name || "Unassigned"}</span></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
